import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { me } from '@/api';
import LanguageSelector from '@/components/LanguageSelector';
import { 
  Send, 
  Users, 
  CheckCircle, 
  Zap,
  MessageCircle,
  Phone,
  Search,
  FileText,
  Handshake,
  ArrowRight,
  Gift
} from 'lucide-react';

const LOGO_URL = '/logo.png';

export default function LandingPage() {
  const { t } = useLanguage();
  const nav = useNavigate();
  
  useEffect(() => {
    const token = sessionStorage.getItem('jc_token');
    if (token) {
      me().then(res => {
        const r = res.data?.role;
        if (r === 'client') nav('/cliente/dashboard');
        else if (r === 'company') nav('/ditta/dashboard');
        else if (r === 'admin') nav('/admin');
      }).catch(() => {
        sessionStorage.removeItem('jc_token');
      });
    }
  }, [nav]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex-shrink-0">
              <img src={LOGO_URL} alt="ServiceGo" className="h-12 sm:h-16 w-auto" />
            </Link>
            
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSelector />
              <Button 
                variant="ghost" 
                onClick={() => nav('/login')}
                className="text-slate-700 hover:text-slate-900"
              >
                Login
              </Button>
              <Button 
                onClick={() => nav('/ditta/registrazione')}
                className="hidden sm:flex bg-[#5848FF] hover:bg-[#4638EF] text-white"
              >
                Registrati come Ditta
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-slate-900 leading-tight">
                Trova il <span className="text-[#5848FF]">professionista</span> giusto in Ticino
              </h1>
              <p className="mt-4 sm:mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto lg:mx-0">
                ServiceGo connette privati e ditte in modo semplice, veloce e diretto. Pubblica la tua richiesta e ricevi contatti da professionisti verificati.
              </p>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg"
                  onClick={() => nav('/cliente/nuova')}
                  className="h-14 px-8 text-lg bg-[#5848FF] hover:bg-[#4638EF] text-white shadow-lg shadow-indigo-200"
                >
                  <Send className="mr-2 w-5 h-5" />
                  Inserisci Richiesta
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => nav('/ditta/registrazione')}
                  className="h-14 px-8 text-lg border-2 border-[#5848FF] text-[#5848FF] hover:bg-indigo-50"
                >
                  Registrati come Ditta
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
              
              {/* Trust badges */}
              <div className="mt-8 flex flex-wrap gap-6 justify-center lg:justify-start text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>{t('ui.freeForClients')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  <span>{t('ui.fastReplies')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                  <span>{t('ui.directContact')}</span>
                </div>
              </div>
            </div>
            
            {/* Hero Image/Illustration */}
            <div className="hidden lg:flex justify-center items-center">
              <div className="relative w-full max-w-lg">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-3xl blur-3xl opacity-20"></div>
                <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-slate-100">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        <FileText className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800">Richiesta pubblicata</div>
                        <div className="text-sm text-slate-500">Idraulico per bagno</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Phone className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-green-700">3 ditte ti hanno contattato!</div>
                        <div className="text-sm text-green-600">Rispondi quando vuoi</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                        <Handshake className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800">Scegli liberamente</div>
                        <div className="text-sm text-slate-500">Nessun obbligo</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works - 2 Columns */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Come funziona</h2>
            <p className="mt-4 text-lg text-slate-600">Semplice per tutti, sia per chi cerca che per chi offre servizi</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
            {/* For Clients */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Per i Privati</h3>
              </div>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm font-bold text-indigo-600">1</div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Inserisci richiesta</h4>
                    <p className="text-slate-600 text-sm mt-1">Descrivi di cosa hai bisogno in pochi click</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm font-bold text-indigo-600">2</div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Ricevi contatti</h4>
                    <p className="text-slate-600 text-sm mt-1">Le ditte interessate ti contattano direttamente</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm font-bold text-indigo-600">3</div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Scegli liberamente</h4>
                    <p className="text-slate-600 text-sm mt-1">Decidi tu con chi lavorare, senza obblighi</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-white rounded-xl border border-indigo-100">
                <div className="flex items-center gap-2 text-green-600 font-semibold">
                  <Gift className="w-5 h-5" />
                  <span>100% Gratuito per i privati</span>
                </div>
              </div>
            </div>
            
            {/* For Companies */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Per le Ditte</h3>
              </div>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm font-bold text-emerald-600">1</div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Consulta richieste</h4>
                    <p className="text-slate-600 text-sm mt-1">Trova clienti nella tua zona e categoria</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm font-bold text-emerald-600">2</div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Contatta direttamente</h4>
                    <p className="text-slate-600 text-sm mt-1">Chiama o scrivi su WhatsApp al cliente</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm font-bold text-emerald-600">3</div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Trova nuovi lavori</h4>
                    <p className="text-slate-600 text-sm mt-1">Espandi la tua clientela in modo semplice</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-white rounded-xl border border-emerald-100">
                <div className="flex items-center justify-between">
                  <span className="text-slate-700 font-medium">Abbonamento mensile</span>
                  <span className="text-2xl font-bold text-emerald-600">49 CHF</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why ServiceGo */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Perché ServiceGo</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="font-bold text-slate-800 mb-2">Veloce</h3>
              <p className="text-slate-600 text-sm">Pubblica in 2 minuti, ricevi contatti subito</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-bold text-slate-800 mb-2">Semplice</h3>
              <p className="text-slate-600 text-sm">Nessuna complicazione, tutto intuitivo</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="font-bold text-slate-800 mb-2">Contatto Diretto</h3>
              <p className="text-slate-600 text-sm">Parla direttamente con chi ti interessa</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Gift className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="font-bold text-slate-800 mb-2">Gratuito</h3>
              <p className="text-slate-600 text-sm">Nessuna commissione per i privati</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Prezzi Trasparenti</h2>
            <p className="mt-4 text-lg text-slate-600">Nessun costo nascosto, nessuna sorpresa</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Privati */}
            <div className="bg-white rounded-3xl p-8 border-2 border-slate-200 hover:border-indigo-300 transition-colors">
              <div className="text-center">
                <h3 className="text-xl font-bold text-slate-800">Privati</h3>
                <div className="mt-4">
                  <span className="text-5xl font-extrabold text-slate-900">Gratis</span>
                </div>
                <p className="mt-2 text-slate-600">Per sempre</p>
              </div>
              <ul className="mt-8 space-y-4">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>{t('ui.unlimitedRequests')}</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>{t('ui.receiveContacts')}</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>{t('ui.zeroCommissions')}</span>
                </li>
              </ul>
              <Button 
                className="w-full mt-8 h-12 bg-slate-800 hover:bg-slate-900 text-white"
                onClick={() => nav('/cliente/registrazione')}
              >
                Registrati Gratis
              </Button>
            </div>
            
            {/* Ditte */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-8 text-white relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-sm font-semibold">
                3 mesi gratis
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold">Ditte</h3>
                <div className="mt-4">
                  <span className="text-5xl font-extrabold">49</span>
                  <span className="text-xl ml-1">CHF/mese</span>
                </div>
                <p className="mt-2 text-emerald-100">Dopo la prova gratuita</p>
              </div>
              <ul className="mt-8 space-y-4">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-200 flex-shrink-0" />
                  <span>{t('ui.accessAllRequests')}</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-200 flex-shrink-0" />
                  <span>{t('ui.contactClientsDirectly')}</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-200 flex-shrink-0" />
                  <span>{t('ui.zeroCommissions')}</span>
                </li>
              </ul>
              <Button 
                className="w-full mt-8 h-12 bg-white text-emerald-600 hover:bg-emerald-50 font-semibold"
                onClick={() => nav('/ditta/registrazione')}
              >
                Prova Gratis 3 Mesi
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[#5848FF] to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Pronto a iniziare?
          </h2>
          <p className="text-xl text-indigo-100 mb-10">
            Unisciti a ServiceGo oggi stesso e trova il professionista giusto per te.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => nav('/cliente/nuova')}
              className="h-14 px-10 text-lg bg-white text-[#5848FF] hover:bg-indigo-50"
            >
              <Send className="mr-2 w-5 h-5" />
              Inserisci Richiesta
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => nav('/ditta/registrazione')}
              className="h-14 px-10 text-lg border-2 border-white text-white hover:bg-white/10"
            >
              Registrati come Ditta
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <img src={LOGO_URL} alt="ServiceGo" className="h-10 brightness-0 invert opacity-80" />
            <div className="flex gap-6 text-sm">
              <Link to="/termini" className="hover:text-white transition-colors">Termini e Condizioni</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm">
            <p>© 2025 ServiceGo - Tutti i diritti riservati</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
